import is from '@sindresorhus/is';
import {
    $createRangeSelection,
    $getRoot,
    $getSelection,
    $isDecoratorNode,
    $isElementNode,
    $isRangeSelection,
    $isTextNode,
    $setSelection,
    type ElementNode,
    type LexicalNode,
    type NodeKey,
} from 'lexical';

import { $createCommentMarkNode, $isCommentMarkNode } from './CommentMarkNode';

type TextPoint = {
    readonly key: NodeKey;
    readonly offset: number;
};

const $resolveWrappableTarget = (node: LexicalNode): LexicalNode | null => {
    if ($isTextNode(node)) {
        return node;
    }

    if (($isElementNode(node) || $isDecoratorNode(node)) && node.isInline()) {
        return node;
    }

    return null;
};

export const $wrapSelectionInCommentMark = (commentId: string): boolean => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        return false;
    }

    const isBackward = selection.isBackward();
    const forwardSelection = $createRangeSelection();
    const [startPoint, endPoint] = isBackward
        ? [selection.focus, selection.anchor]
        : [selection.anchor, selection.focus];

    forwardSelection.anchor.set(startPoint.key, startPoint.offset, startPoint.type);
    forwardSelection.focus.set(endPoint.key, endPoint.offset, endPoint.type);

    let currentNodeParent: ElementNode | null | undefined;
    let lastCreatedMarkNode: ElementNode | undefined;

    for (const node of forwardSelection.extract()) {
        if ($isElementNode(lastCreatedMarkNode) && lastCreatedMarkNode.isParentOf(node)) {
            continue;
        }

        if ($isCommentMarkNode(node)) {
            continue;
        }

        const targetNode = $resolveWrappableTarget(node);

        if (targetNode === null) {
            currentNodeParent = undefined;
            lastCreatedMarkNode = undefined;
            continue;
        }

        if (targetNode.is(currentNodeParent)) {
            continue;
        }

        const parentNode = targetNode.getParent();

        if (parentNode === null || !parentNode.is(currentNodeParent)) {
            lastCreatedMarkNode = undefined;
        }

        currentNodeParent = parentNode;

        if (lastCreatedMarkNode === undefined) {
            lastCreatedMarkNode = $createCommentMarkNode(commentId);
            targetNode.insertBefore(lastCreatedMarkNode);
        }

        lastCreatedMarkNode.append(targetNode);
    }

    if (!$isElementNode(lastCreatedMarkNode)) {
        return false;
    }

    if (isBackward) {
        lastCreatedMarkNode.selectStart();
    } else {
        lastCreatedMarkNode.selectEnd();
    }

    return true;
};

export const $wrapTextRangeInCommentMark = (start: number, end: number, commentId: string): boolean => {
    if (end <= start) {
        return false;
    }

    let offset = 0;
    let anchor: TextPoint | null = null;
    let focus: TextPoint | null = null;

    for (const node of $getRoot().getAllTextNodes()) {
        const nodeStart = offset;
        const nodeEnd = offset + node.getTextContentSize();

        if (is.null(anchor) && nodeEnd > start) {
            anchor = { key: node.getKey(), offset: start - nodeStart };
        }

        if (!is.null(anchor) && nodeEnd >= end) {
            focus = { key: node.getKey(), offset: end - nodeStart };
            break;
        }

        offset = nodeEnd;
    }

    if (is.null(anchor) || is.null(focus)) {
        return false;
    }

    const selection = $createRangeSelection();
    selection.anchor.set(anchor.key, anchor.offset, 'text');
    selection.focus.set(focus.key, focus.offset, 'text');
    $setSelection(selection);

    return $wrapSelectionInCommentMark(commentId);
};
